import { hrApiClient } from '@/lib/api';
import {
  CreateCvDataDto,
  CvDataType,
  CvExperienceEndorsementInputDto,
  CvSkillEndorsementInputDto,
  DeleteCvDataDto,
  UpdateCvDataDto,
} from '@/shared/cv/cv.dto';
import { IFilter, IItemResponse, IListResponse } from '@/shared/base/base.dto';
import {
  CvSchoolOption,
  CvSchoolSearchParams,
  CvSearchSkillItem,
  CvSkillSearchParams,
  CvVisibleCoursePayload,
  CvVisibleExamPayload,
  CvVisibleTestlyPayload,
} from '@/types/cv';
import {
  PositionOption,
  PositionsResponse,
} from '@/features/cv/types/position.types';
import { CompanyListDTO } from '@/shared';
import { OfferListDTO } from '@/shared/job/job.offers.dto';
import { EsCvData } from '@/shared/cv/cv.es.dto';

interface CvSchoolApiItem {
  id: number;
  name_mn?: string;
  name_en?: string;
  title_mn?: string;
  title_en?: string;
}

function normalizeSchoolOption(item: CvSchoolApiItem): CvSchoolOption {
  return {
    id: item.id,
    name_mn: item.name_mn ?? item.title_mn ?? '',
    name_en:
      item.name_en ?? item.title_en ?? item.name_mn ?? item.title_mn ?? '',
  };
}

export const fetchCvDetail = async (): Promise<EsCvData | null> => {
  const { data } = await hrApiClient.get('/user/cv/detail');
  return data.data;
};

export const createCvByType = async (
  type: string,
  data: CreateCvDataDto
): Promise<EsCvData> => {
  const { data: response } = await hrApiClient.post(
    `/user/cv/create/${type}`,
    data.data
  );
  return response.data;
};

export const updateCvByType = async (
  data: UpdateCvDataDto
): Promise<EsCvData> => {
  const { data: response } = await hrApiClient.post(
    `/user/cv/update/${data.type}`,
    data.data
  );
  return response.data;
};

export const deleteCvByType = async (
  data: DeleteCvDataDto
): Promise<EsCvData> => {
  const { data: response } = await hrApiClient.post(
    `/user/cv/delete/${data.type}`,
    data.data
  );
  return response.data;
};

export const fetchCvCompanies = async (
  search?: string,
  limit?: number,
  page?: number
) => {
  const params: { limit: number; page: number; search?: string } = {
    limit: limit ?? 50,
    page: page ?? 1,
  };
  if (search?.trim()) {
    params.search = search.trim();
  }
  const { data } = await hrApiClient.get('/data/cv_companies', { params });
  return data.data ?? [];
};

type CvPositionsPayload =
  | PositionOption[]
  | PositionsResponse
  | { data?: PositionOption[] | PositionsResponse };

function extractCvPositionItems(
  payload: CvPositionsPayload | undefined
): PositionOption[] {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  if ('items' in payload && Array.isArray(payload.items)) {
    return payload.items;
  }

  if ('data' in payload && payload.data !== undefined) {
    const nested = payload.data;
    if (Array.isArray(nested)) return nested;
    if ('items' in nested && Array.isArray(nested.items)) {
      return nested.items;
    }
  }

  return [];
}

export const fetchCvPositions = async (query?: string, limit?: number) => {
  const params: { limit: number; search?: string } = { limit: limit ?? 50 };
  if (query?.trim()) {
    params.search = query.trim();
  }

  const { data } = await hrApiClient.get<CvPositionsPayload>(
    '/data/search-professions',
    { params }
  );

  return extractCvPositionItems(data);
};

export async function createInitialCv(langId = 1) {
  const result = await createCvByType(CvDataType.CV, {
    langId,
    data: { langId },
  });
  return { data: { createCV: { id: result?.id, success: true } } };
}

export const fetchCvSkills = async (
  params: CvSkillSearchParams
): Promise<CvSearchSkillItem[]> => {
  const { data } = await hrApiClient.get(`/data/cv_skills`, { params });
  return (data?.data ?? []) as CvSearchSkillItem[];
};

export const fetchCvSchools = async ({
  countryId,
  categoryId,
  search,
}: CvSchoolSearchParams): Promise<CvSchoolOption[]> => {
  const params: {
    countryId: number;
    categoryId: number;
    limit: number;
    search?: string;
  } = {
    countryId,
    categoryId,
    limit: 50,
  };

  if (search?.trim()) {
    params.search = search.trim();
  }

  const { data } = await hrApiClient.get('/data/cv_schools', { params });
  const items = (data.data ?? []) as CvSchoolApiItem[];
  return items.map(normalizeSchoolOption);
};

export const uploadTrainingCertificate = async (
  formData: FormData
): Promise<EsCvData> => {
  const { data } = await hrApiClient.post(
    '/user/cv/training-certificate',
    formData
  );
  return data.data;
};

export const uploadProfileImage = async (
  formData: FormData
): Promise<EsCvData> => {
  const { data } = await hrApiClient.post('/user/cv/upload-profile', formData);
  return data.data;
};

export const cvExperienceEndorsement = async (
  body: CvExperienceEndorsementInputDto
): Promise<EsCvData> => {
  const { data } = await hrApiClient.post(
    '/user/cv/endorsement/experience',
    body
  );
  return data.data;
};

export const cvSkillEndorsement = async (
  body: CvSkillEndorsementInputDto
): Promise<EsCvData> => {
  const { data } = await hrApiClient.post('/user/cv/endorsement/skill', body);
  return data.data;
};

export const updateCvVisibleCourse = async (
  body: CvVisibleCoursePayload
): Promise<IItemResponse<EsCvData | undefined>> => {
  const { data } = await hrApiClient.post('/user/cv/visible-course', body);
  return data;
};
export const updateCvVisibleExam = async (
  body: CvVisibleExamPayload
): Promise<IItemResponse<EsCvData | undefined>> => {
  const { data } = await hrApiClient.post('/user/cv/visible-exam', body);
  return data;
};

export const updateCvVisibleTestly = async (
  body: CvVisibleTestlyPayload
): Promise<IItemResponse<EsCvData | undefined>> => {
  const { data } = await hrApiClient.post('/user/cv/visible-testly', body);
  return data;
};

export const getCvReviewers = async (
  params: IFilter
): Promise<IListResponse<CompanyListDTO & { time: number }>> => {
  const { data } = await hrApiClient.get('/user/cv/cv-viewed-companies', {
    params,
  });
  return data as IListResponse<CompanyListDTO & { time: number }>;
};

export const getJobOfferList = async (
  params: IFilter
): Promise<IListResponse<OfferListDTO>> => {
  const { data } = await hrApiClient.get('/user/cv/cv-offers-list', { params });
  return data as IListResponse<OfferListDTO>;
};

export interface AcceptJobOfferPayload {
  offerId: number;
  status: 1 | 2 | 3;
}

export const acceptJobOffer = async (
  data: AcceptJobOfferPayload
): Promise<EsCvData> => {
  const { data: response } = await hrApiClient.post(
    '/user/cv/cv-offers-accept',
    data
  );
  return response.data;
};
